'use client';

import FaIcon, { faChevronRight, faXmark } from '@/components/icon/FaIcon/FaIcon';
import NextImageView from '@/components/ui/NextImageView/NextImageView';
import type { Product } from '@/types';
import { memo, useEffect, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import styles from './ItemFilterModal.module.scss';

// フィルタを開くボタンの文言（ItemFilterTrigger）と揃える。SP / PC 共通。
const MODAL_TITLE = 'つくりたいアイテムで絞り込む';
// 開閉アニメーションの長さ（scss の transition と揃える）
export const TRANSITION_MS = 200;

function ItemFilterModal({
  isOpen,
  onClose,
  products,
  selectedProductIds,
  onToggleProduct,
  onClear,
}: {
  isOpen: boolean;
  onClose: () => void;
  products: Product[];
  selectedProductIds: number[];
  onToggleProduct: (productId: number) => void;
  onClear: () => void;
}) {
  const nodeRef = useRef<HTMLDivElement>(null);
  // スクロールロックの元の値と、閉じ際の遅延復元タイマー
  const originalOverflowRef = useRef<string | null>(null);
  const restoreScrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    // 連続開閉で復元が予約済みならキャンセルし、ロックを維持する
    if (restoreScrollTimerRef.current) {
      clearTimeout(restoreScrollTimerRef.current);
      restoreScrollTimerRef.current = null;
    }
    if (originalOverflowRef.current === null) {
      originalOverflowRef.current = document.body.style.overflow;
    }
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // exit アニメーション(TRANSITION_MS)の間は背面をロックしたままにし、閉じ切ってから復元する
      restoreScrollTimerRef.current = setTimeout(() => {
        if (originalOverflowRef.current !== null) {
          document.body.style.overflow = originalOverflowRef.current;
          originalOverflowRef.current = null;
        }
        restoreScrollTimerRef.current = null;
      }, TRANSITION_MS);
    };
  }, [isOpen, onClose]);

  const modalTitle = MODAL_TITLE;
  const hasSelection = selectedProductIds.length > 0;

  const handleClear = () => {
    onClear();
    onClose();
  };

  return (
    <CSSTransition in={isOpen} timeout={TRANSITION_MS} classNames={styles} nodeRef={nodeRef} appear unmountOnExit>
      <div ref={nodeRef} className={styles.root}>
        <div className={styles.overlay} onClick={onClose} aria-hidden='true' />
        <dialog open className={styles.modalBox} aria-label={modalTitle}>
          <div className={styles.header}>
            <h2 className={styles.title}>{modalTitle}</h2>
            <button type='button' className={styles.closeButton} onClick={onClose} aria-label='閉じる'>
              <FaIcon icon={faXmark} />
            </button>
          </div>
          <ul className={styles.productList}>
            {products.map((product) => {
              const isSelected = selectedProductIds.includes(product.id);
              // 何かが選択されているとき、非選択アイテムは darkgray で控えめにする
              const isDimmed = hasSelection && !isSelected;
              return (
                <li key={product.id}>
                  <button
                    type='button'
                    className={`${styles.productRow} ${isSelected ? styles.productRowSelected : ''} ${isDimmed ? styles.productRowDimmed : ''}`}
                    onClick={() => onToggleProduct(product.id)}
                  >
                    <div className={styles.productIconWrapper}>
                      <NextImageView
                        src={product.imageUrls.iconSub}
                        alt={product.name}
                        fill={false}
                        width={32}
                        height={32}
                        objectFit='contain'
                        className={styles.productIcon}
                      />
                    </div>
                    <span className={styles.productName}>{product.name}</span>
                    <span className={styles.chevron}>
                      <FaIcon icon={faChevronRight} />
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          <div className={styles.footer}>
            <button type='button' className={styles.clearButton} onClick={handleClear}>
              クリア
            </button>
          </div>
        </dialog>
      </div>
    </CSSTransition>
  );
}

export default memo(ItemFilterModal);
